'use client';

import { NextPage } from 'next';
import Head from 'next/head';
import { useState } from 'react';

import { Button, Grid, Stack, Typography } from '@mui/material';

import AddIcon from '@mui/icons-material/AddRounded';

import 'ag-grid-enterprise';

import useUser from '../../auth/useUser';

import { Forbidden, Policy, PolicyMatchMode, useDialogController } from 'ui';

import { RolePermission, OrderDto, OrderState } from 'desk-common';

import CreateOrderModal from '../../components/orders/CreateOrderModal';
import OrderSummaryModal from '../../components/orders/OrderSummaryModal';

import OrdersTable from '../../components/orders/OrdersTable';
import { useOrders } from '../../hooks/useOrders';
import { OrdersStats } from '../../components/orders/OrdersStats';
import userHasPermission from '../../utils/functions/userHasPermission';

const OrdersListPage: NextPage = () => {
  const user = useUser();

  const {
    isOpen: isOpenCreate,
    open: openCreate,
    close: closeCreate,
  } = useDialogController();

  const {
    isOpen: isOpenDetail,
    open: openDetail,
    close: closeDetail,
  } = useDialogController();

  const [selectedOrder, setSelectedOrder] = useState<OrderDto | null>(null);
  const [tableFilterState, setTableFilterState] = useState<OrderState | null>(
    null
  );

  const {
    data: rowData = [],
    isLoading,
    isFetching,
  } = useOrders(
    {},
    {
      enabled: userHasPermission(
        user,
        [RolePermission.readOrders, RolePermission.readOwnOrders],
        'ANY'
      ),
    }
  );

  const handleCreate = () => {
    setSelectedOrder(null);
    openCreate();
  };

  return (
    <>
      <Head>
        <title>Pedidos</title>
      </Head>

      <Policy
        user={user}
        roles={[RolePermission.readOrders, RolePermission.readOwnOrders]}
        matchMode={PolicyMatchMode.ANY}
        fallback={
          <Forbidden
            text="No tienes permisos para ver órdenes"
            email={user?.email}
          />
        }
      >
        <Grid
          container
          spacing={4}
          sx={{
            minHeight: '100vh',
            p: { xs: 2, md: 4 },
          }}
        >
          <Grid size={12}>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              justifyContent="space-between"
              alignItems="center"
              spacing={2}
            >
              <Stack>
                <Typography variant="h4" fontWeight={700}>
                  Pedidos
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Gestión y seguimiento de órdenes
                </Typography>
              </Stack>

              <Policy roles={[RolePermission.createOrders]} user={user}>
                <Button
                  variant="contained"
                  size="large"
                  startIcon={<AddIcon />}
                  onClick={handleCreate}
                  sx={{
                    borderRadius: 3,
                    textTransform: 'none',
                    fontWeight: 600,
                    px: 3,
                    boxShadow: '0 8px 24px rgba(25,118,210,0.25)',
                  }}
                >
                  Crear pedido
                </Button>
              </Policy>
            </Stack>
          </Grid>

          <Grid size={12}>
            <OrdersStats
              orders={rowData}
              onStatClick={setTableFilterState}
              selectedState={tableFilterState}
            />
          </Grid>

          <Grid size={12} sx={{ height: 'calc(100vh - 150px)' }}>
            <OrdersTable
              rowData={rowData}
              isLoading={isLoading || isFetching}
              user={user}
              onEdit={(order) => {
                setSelectedOrder(order);
                openCreate();
              }}
              onView={(order) => {
                setSelectedOrder(order);
                openDetail();
              }}
              filterState={tableFilterState}
            />
          </Grid>
        </Grid>
      </Policy>

      {isOpenCreate && (
        <CreateOrderModal
          open={isOpenCreate}
          onClose={() => {
            closeCreate();
            setSelectedOrder(null);
          }}
          order={selectedOrder}
        />
      )}

      {isOpenDetail && (
        <OrderSummaryModal
          open={isOpenDetail}
          onClose={() => {
            closeDetail();
            setSelectedOrder(null);
          }}
          order={selectedOrder}
        />
      )}
    </>
  );
};

export default OrdersListPage;
